import { Download, LockKeyhole } from 'lucide-react';
import { DownloadRecords } from './DownloadRecords';
import { useEffect, useState } from 'react';
import { api, tokens } from '../lib/api';
import { Collapsible, Fold } from '../ui/Collapsible';
import { useParent } from './ParentContext';
import { MfaSettings } from './TwoFactor';
import { PasskeySettings } from './Passkeys';

/**
 * Settings → Account security (#415): change your password, and two-factor sign-in, in one place. Each part with more
 * than one control is a fold (#601); a link to #twofactor or #passkeys opens the section and that fold.
 */
export function AccountSecurity() {
  const { parent } = useParent();
  return (
    <Collapsible id="security" title="Account security" icon={<LockKeyhole size={18} />} anchors={['twofactor', 'passkeys']}>
      <Fold title="Password" summary="Change your password">
        <ChangePassword />
      </Fold>
      <PasskeySettings />
      <MfaSettings />
      <Fold title="Download my records" summary="Everything, as one file">
        <DownloadRecords heading={false} />
      </Fold>
      <ExportData plain />
      <Fold title="Delete my account" summary={parent.deleteAt ? `Scheduled for ${new Date(parent.deleteAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : undefined}>
        <DeleteAccount />
      </Fold>
    </Collapsible>
  );
}

function ChangePassword() {
  const { toast } = useParent();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && confirm !== next;

  async function save() {
    setBusy(true);
    try {
      await api.post('/api/me/password', { current, password: next });
      setCurrent('');
      setNext('');
      setConfirm('');
      toast({ text: 'Password changed. Any other devices were signed out.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="stack"
      aria-label="Change password"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <label>
        Current password
        <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
      </label>
      <label>
        New password <span className="muted small">(at least 8 characters)</span>
        <input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" aria-invalid={tooShort} />
      </label>
      <label>
        Confirm new password
        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" aria-invalid={mismatch} />
        {mismatch && <span className="small bad-text">The new passwords don’t match.</span>}
      </label>
      <p className="muted small">Changing it signs out your other devices. You’ll get an email to say it changed.</p>
      <div>
        <button className="btn primary" type="submit" disabled={busy || !current || next.length < 8 || confirm !== next}>
          {busy ? 'Saving…' : 'Change password'}
        </button>
      </div>
    </form>
  );
}

/**
 * Delete my account (#518, GDPR): the account and all its data are erased after a grace period (signing in before then
 * keeps it). A family you own that other adults are in has to go to one of them first.
 */
function DeleteAccount() {
  const { parent, toast, signOut } = useParent();
  const [info, setInfo] = useState<{ graceDays: number; blockers: { id: string; name: string }[] } | null>(null);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) api.get<{ graceDays: number; blockers: { id: string; name: string }[] }>('/api/me/delete').then(setInfo).catch(() => undefined);
  }, [open]);

  async function remove() {
    if (!confirm('Delete your ClassGuardian account and all its data? Once it’s erased it can’t be undone.')) return;
    setBusy(true);
    try {
      const r = await api.post<{ deleted?: boolean; deleteAt?: number }>('/api/me/delete', { password, code: code || undefined });
      toast({
        text: r.deleted ? 'Your account and its data were deleted.' : `Your account will be deleted on ${new Date(r.deleteAt!).toLocaleDateString()}. Sign in before then to keep it.`,
        tone: 'good',
      });
      signOut();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    // Inside the "Delete my account" fold in Account security (#601), which gives it its title.
    <div className="stack delete-account">
      {!open ? (
        <div>
          <p className="muted small">Erase your account and everything in it. You can change your mind until it happens.</p>
          <button className="btn danger-text" onClick={() => setOpen(true)}>
            Delete my account…
          </button>
        </div>
      ) : (
        <>
          <p className="small">
            This erases your account, your sign-in history, passkeys and two-factor, notifications, API keys and webhooks, and your Help &amp; Feedback
            requests. A family you own goes too, with its children’s activity, grades, planner, journal and devices. Children you added to someone
            else’s family stay with that family.
          </p>
          {info && info.graceDays > 0 && (
            <p className="muted small">It happens {info.graceDays} days after you ask, and you’re signed out now. Sign in before then and nothing is deleted.</p>
          )}
          {!!info?.blockers.length && (
            <p className="error small">
              Other adults are in {info.blockers.map((b) => b.name).join(' and ')}. Make one of them the owner first in Settings → Family and Sharing.
            </p>
          )}
          <label>
            Your password
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          </label>
          {parent.mfaEnabled && (
            <label>
              Code from your authenticator app
              <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" />
            </label>
          )}
          <div className="row-gap">
            <button className="btn danger-text" onClick={remove} disabled={busy || !password || !!info?.blockers.length}>
              {busy ? 'One moment…' : 'Delete my account'}
            </button>
            <button className="btn" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Export my data (#521, GDPR): everything about you and the children you can see, as one JSON file. `plain` drops the
 * heading, for Settings → Account security where it's a single row among folds (#601).
 */
export function ExportData({ plain = false }: { plain?: boolean }) {
  const { toast } = useParent();
  const [busy, setBusy] = useState(false);
  async function download() {
    setBusy(true);
    try {
      const res = await fetch('/api/me/export', { headers: { Authorization: `Bearer ${tokens.parent() ?? ''}` } });
      if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error ?? 'Couldn’t export your data.');
      const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'classguardian-my-data.json';
      const url = URL.createObjectURL(await res.blob());
      const a = Object.assign(document.createElement('a'), { href: url, download: name });
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className={`stack export-data ${plain ? '' : 'sub-section'}`}>
      {!plain && (
        <h3 className="with-icon">
          <Download size={16} /> Export my data
        </h3>
      )}
      <p className="muted small">
        Download everything ClassGuardian holds about you and the children you can see (account, families, schedules, activity, planner, grades,
        attendance, reading, journal and your Help &amp; Feedback requests) as one file. Passwords and keys are never included.
      </p>
      <div>
        <button className="btn" onClick={download} disabled={busy}>
          <Download size={15} /> {busy ? 'Preparing…' : 'Download my data'}
        </button>
      </div>
    </div>
  );
}
